import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  Inject,
  InjectionToken,
  PLATFORM_ID,
  Optional,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { Collection, Filter, FilterValue, LoadFilterdProductsByQuery, LoadProductsFiltered, Pagination, Product } from '../../../shop';
import * as fromShopify from '../../../shop';
// import { CardGridAnimation } from '../../../animations';
import { CloseFilters, Go, OpenFilters, SetCanonicalUrl, SetTags, SetTitle } from '../../../store';
import { ActivatedRoute, Router } from '@angular/router';
import { map, takeUntil, withLatestFrom, take, pairwise, filter, startWith } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Breadcrumb } from 'src/app/core/models/breadcrumb.model';
import { ProductsService } from 'src/app/shop/services';
import { ProductFiltersComponent } from '../../components/product-filters/product-filters.component';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { ShopifyMetaFieldsPipe } from 'src/app/shop/pipes/shopify-metafields.pipe';
import { CookiePreferences, getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';
import { isPlatformBrowser } from '@angular/common';
import { Analyzify } from 'src/app/utility/analyzify.utility';
import { REQUEST } from '../../../../express.tokens';
import { Request } from 'express';
import { convertSchemaToHtml } from '@thebeyondgroup/shopify-rich-text-renderer';
import { StringUtility } from '@teamfoster/sdk/text-utility';

@Component({
  selector: 'app-product-catalog',
  templateUrl: './product-catalog.component.html',
  // animations: [trigger('CardGridAnimation', [CardGridAnimation])],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductCatalogComponent implements OnInit, OnDestroy {
  @ViewChild('filterComponent', { static: false }) filters?: ProductFiltersComponent;
  private dict = new FromDictionaryPipe(this.lang);
  private meta = new ShopifyMetaFieldsPipe();

  cookiePrefs$!: Observable<CookiePreferences | null>;
  queryParams$!: Observable<{ [key: string]: any }>;
  searchQuery?: string;
  products$!: Observable<Product[]>;
  page$!: Observable<
    | {
        cursor: string;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      }
    | undefined
  >;
  filteredPage$!: Observable<
    | {
        cursor: string;
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      }
    | undefined
  >;
  products: any = [];
  selectedCollection$?: Observable<Collection | undefined>;

  page: Pagination = {
    current: 0,
    size: 1,
    pageCount: 5,
    resultCount: 5,
  };

  // Structured data
  scheme: any;
  breadcrumbScheme: any;
  breadcrumbs: Breadcrumb[] = [];

  filtersPanelOpen: boolean = false;

  activeFilters: any;
  activeFilters2: FilterValue[] = [];

  private _unsubscribe = new Subject<void>();

  filteredProducts$?: Observable<any>;

  filters$: Observable<Filter[]> = of([]);
  showFilters = true;
  collectionTextExpanded = false;

  loading$: Observable<boolean> = of(true);

  collectionLoading$ = new BehaviorSubject(true);

  constructor(
    private store: Store,
    private router: Router,
    private shopifyService: ProductsService,
    public route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private lang: LanguageService,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<Object>,
    @Optional() @Inject(REQUEST) private request: Request
  ) {}

  ngOnInit(): void {
    this.cookiePrefs$ = this.store.select(getCookiePreferences);
    this.products$ = this.store.select(fromShopify.getAllProducts);
    this.loading$ = this.store.select(fromShopify.getProductsLoading);
    this.selectedCollection$ = this.store.select(fromShopify.getSelectedCollection);
    this.page$ = this.store.select(fromShopify.getProductsPageInfo);
    this.filteredPage$ = this.store.select(fromShopify.getProductsFilteredPageInfo);

    this.filteredProducts$ = this.store.select(fromShopify.getProductsCollectionFiltered);
    this.queryParams$ = this.route.queryParams;

    // Get filters from collection
    this.filters$ = this.selectedCollection$.pipe(
      map(a => (a?.filters || []).filter(filter => filter.values.filter(a => a.count > 0).length > 1)) // Hide filter option if only 1 option has results
    );

    // hide filters when meta options has__filters == false
    this.filters$ = combineLatest([this.filters$, this.selectedCollection$]).pipe(
      map(([filters, col]) => {
        const showFilters = col ? this.meta.transform(col?.metafields, 'has_filters') : true;
        return showFilters !== 'false' ? filters : [];
      })
    );

    // this.products$ =
    this.products$ = combineLatest([this.filteredProducts$, this.products$, this.queryParams$]).pipe(
      map(([filteredProducts, products, params]) =>
        Object.keys(this.activeFilters || {}).length || params['q'] ? filteredProducts : products
      )
    );

    // Analyzify product impressions
    this.products$
      .pipe(
        startWith([]),
        pairwise(),
        map(([prev, current]) => {
          return current.filter(a => !prev.map(b => b.id).includes(a.id));
        }),
        withLatestFrom(this.cookiePrefs$, this.selectedCollection$),
        filter(([products, cookies, collection]) => products.length > 0 && (cookies?.marketing ?? false)),
        takeUntil(this._unsubscribe)
      )
      .subscribe(([products, cookies, collection]) => {
        if (isPlatformBrowser(this.platformId)) {
          Analyzify.CollectionPageView(products, collection);
        }
      });

    // Set structured data
    // SEO stuff
    this.selectedCollection$.pipe(withLatestFrom(this.products$), takeUntil(this._unsubscribe)).subscribe(([collection, products]) => {
      if (collection) {
        console.log('Collection', collection);
        this.collectionLoading$.next(true);

        this.setScheme(collection, products);
        this.setBreadcrumbScheme(collection);
        this.setBreadcrumbs(collection);
        this.setMeta(collection);

        this.activeFilters = null;
        this.activeFilters2 = [];

        this.collectionLoading$.next(false);
        this.cd.detectChanges();
      }
    });

    this.route.queryParams
      .pipe(
        withLatestFrom(this.selectedCollection$, this.filters$),
        // withLatestFrom(this.selectedCollection$),
        takeUntil(this._unsubscribe)
      )
      .subscribe(([params, collection, filters]) => {
        this.activeFilters = undefined;
        if (params['q']) {
          this.searchQuery = params['q'];
          this.store.dispatch(LoadFilterdProductsByQuery({ searchQuery: params['q'] }));

          if (params['q']) {
            this.setBreadcrumbs();
          }
        }
        if (filters?.length > 0) {
          const values = Object.values(params).filter(a => a);
          this.activeFilters = params;
          this.activeFilters2 = (collection?.filters || [])
            .map(a => a.values)
            .flat()
            .filter(a => values.includes(a.id));

          this.store.dispatch(
            LoadProductsFiltered({
              handle: this.route.snapshot.params['collectionHandle'],
              filters: this.activeFilters2.map(a => a.input),
            })
          );
          this.filtersPanelOpen = false;
        }
      });
  }

  async handleAddToCart(product: Product) {}

  loadMore(
    page: Observable<
      | {
          cursor: string;
          hasNextPage: boolean;
          hasPreviousPage: boolean;
        }
      | undefined
    >
  ) {
    page
      .pipe(take(1), withLatestFrom(this.queryParams$, this.loading$, this.selectedCollection$ || of(), this.filters$))
      .subscribe(([pageInfo, queryParams, loading, collection, filters]) => {
        if (loading) {
          return;
        }
        if (queryParams['q']) {
          this.store.dispatch(fromShopify.LoadFilterdProductsByQuery({ searchQuery: queryParams['q'], after: pageInfo?.cursor }));
        }
        if (this.activeFilters2?.length > 0) {
          this.store.dispatch(
            fromShopify.LoadMoreProductsFiltered({
              filters: this.activeFilters2.map(a => a.input),
            })
          );
        } else {
          this.store.dispatch(!!collection ? fromShopify.LoadMoreProductsForCollection() : fromShopify.LoadMoreProducts());
        }
      });
  }

  setMeta(collection: Collection, q?: string) {
    if (collection) {
      // Set metadata
      const hostname = isPlatformBrowser(this.platformId) ? document.location.hostname : this.request?.hostname;

      this.store.dispatch(
        SetTitle({ title: `${collection.seo?.title || collection.title + ' - ' + this.dict.transform('meta-titel-suffix')}` })
      );
      this.store.dispatch(SetCanonicalUrl({ url: `https://${hostname}${this.router.url.split('?')[0]}` }));
      this.store.dispatch(
        SetTags({
          properties: {
            'description': StringUtility.Truncate(
              StringUtility.StripHTML(collection.seo?.description || collection.description),
              157,
              '...'
            ),
            'og:image': '',
            'og:title': `${collection.seo?.title || collection.title + ' - ' + this.dict.transform('meta-titel-suffix')}`,
            'og:description': StringUtility.Truncate(
              StringUtility.StripHTML(collection.seo?.description || collection.description),
              157,
              '...'
            ),
          },
        })
      );
    } else {
      const viewtype = q ? 'zoek' : 'producten';
      // Set metadata
      this.store.dispatch(SetTitle({ title: this.dict.transform(viewtype + '-overzicht-titel') }));
      this.store.dispatch(
        SetTags({
          properties: {
            'description': this.dict.transform(viewtype + '-overzicht-beschrijving') + q ? ` ${q}` : '',
            'og:image': '',
            'og:title': `${this.dict.transform(viewtype + '-overzicht-titel')} - ${this.dict.transform('meta-titel-suffix')}`,
            'og:description': this.dict.transform(viewtype + '-overzicht-beschrijving') + q ? ` ${q}` : '',
          },
        })
      );
    }
  }

  trackByFn(index: number, item: Product) {
    return item?.id;
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  setScheme(collection: Collection, products: Product[]) {
    this.scheme = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'name': collection ? collection.title : 'Alle producten',
      'url': this.router.url,
      'description': 'Alle producten',
      'image': collection ? collection.image?.url : 'https://cdn.shopify.com/s/files/1/0622/3881/8561/collections/placeholder.png',
      'mainEntityOfPage': {
        '@type': 'CollectionPage',
        '@id': environment.storeDomain + this.router.url,
      },
      'itemListElement': products.map((product, i) => ({
        '@type': 'ListItem',
        'position': i + 1,
        'url': collection
          ? environment.storeDomain + '/collections/' + collection.handle + '/' + product?.handle
          : environment.storeDomain + '/products' + product?.handle,
      })),
    };
  }

  setBreadcrumbs(collection?: Collection) {
    if (collection) {
      this.breadcrumbs = [
        {
          label: 'Collecties',
          routerLink: ['/collections'],
        },
        {
          label: collection?.title,
          routerLink: ['/collections', collection?.handle],
        },
      ];
    } else {
      this.breadcrumbs = [
        {
          label: 'zoekresultaten',
          routerLink: [],
        },
      ];
    }
  }
  setBreadcrumbScheme(collection: Collection) {
    this.breadcrumbScheme = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'item': {
            '@type': 'Website',
            '@id': environment.storeDomain,
            'name': environment.storeName,
          },
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'item': {
            '@type': 'CollectionPage',
            '@id': environment.storeDomain + this.router.url,
            'name': collection ? collection.title : 'Alle producten',
          },
        },
      ],
    };
  }

  handleFilterForm(e: any) {
    this.activeFilters = e.value;
    this.cd.detectChanges();

    if (!this.filtersPanelOpen) {
      // form values to queryParams
      const params = Object.fromEntries(Object.entries(e.value).filter(([_, v]) => v != null));
      this.store.dispatch(Go({ path: [], queryParams: params }));
    }
  }

  get activeFiltersLength() {
    if (!this.activeFilters && !this.searchQuery) {
      return 0;
    } else if (!this.activeFilters && this.searchQuery) {
      return 1;
    }
    return Object.keys(this.activeFilters).filter(a => this.activeFilters[a]).length + (this.searchQuery ? 1 : 0);
  }

  applyFilters() {
    const params = Object.fromEntries(Object.entries(this.activeFilters).filter(([_, v]) => v != null));
    this.store.dispatch(Go({ path: [], queryParams: params }));
    this.store.dispatch(CloseFilters());
  }

  resetFilters() {
    this.filters?.reset();
    this.activeFilters = null;
    this.activeFilters2 = [];
    this.setFilterPanelState(false);
    this.cd.detectChanges();
  }

  setFilterPanelState(open: boolean) {
    this.filtersPanelOpen = open;
    this.store.dispatch(this.filtersPanelOpen ? OpenFilters() : CloseFilters());
  }

  getLength(a: any) {
    return Object.keys(a);
  }

  removeFilter(filter: FilterValue) {
    // Find key by value
    const filterId = Object.keys(this.activeFilters).find(key => this.activeFilters[key] === filter.id);

    this.filters?.form?.form.get(filterId || '')?.reset();
    this.cd.detectChanges();
  }
  getCollectionImage(collection: Collection) {
    return collection?.metafield?.reference['image'];
  }

  getHtmlFromRichText(richText: string) {
    if (richText == '' || richText == null || richText == undefined) return '';
    return convertSchemaToHtml(richText);
  }
}
